import type { TFunction } from 'i18next';
import {
  FacilityFlowCoreModulesFleetDtosVehicleLogbookIssue as Issue,
  FacilityFlowCoreModulesFleetEntitiesVehicleLogbookField as Field,
} from '@/api/generated/models';
import type { VehicleLogbookEntryDto, VehicleLogbookRevisionDto } from '@/store/useVehicleLogbookStore';
import { formatDateTime, parseUtcTimestamp } from '@/utils/dateUtils';
import { formatKmNumber } from '@/utils/distance';
import { MISSING_NUMBER } from '@/utils/locale';

const FIELD_KEYS: Record<number, string> = {
  [Field.NUMBER_1]: 'startKm',
  [Field.NUMBER_2]: 'endKm',
  [Field.NUMBER_3]: 'destination',
  [Field.NUMBER_4]: 'purpose',
  [Field.NUMBER_6]: 'startedBy',
  [Field.NUMBER_7]: 'endedBy',
  [Field.NUMBER_8]: 'departure',
  [Field.NUMBER_9]: 'arrival',
  [Field.NUMBER_11]: 'startLocation',
  [Field.NUMBER_12]: 'purposeKind',
};

/** Stored values of TripPurposeKind → their `vehicles:trip.kind.*` key. */
const KIND_KEYS: Record<string, string> = { '1': 'business', '2': 'private' };

const KM_FIELDS: number[] = [Field.NUMBER_1, Field.NUMBER_2];
const TIME_FIELDS: number[] = [Field.NUMBER_8, Field.NUMBER_9];

/**
 * "km Ende: 45.278 → 45.287" — one changed field of a logbook entry. Times travel as UTC and
 * are shown in local time; a cancelled trip has its own line.
 */
export function describeRevision(revision: VehicleLogbookRevisionDto, t: TFunction): string {
  const field = revision.field ?? 0;
  if (field === Field.NUMBER_10) return t('vehicles:logbook.revisionCancelled');
  const isKm = KM_FIELDS.includes(field);
  const isTime = TIME_FIELDS.includes(field);
  const value = (raw?: string | null) => {
    if (raw == null || raw === '') return MISSING_NUMBER;
    if (isTime) return formatDateTime(parseUtcTimestamp(raw));
    if (field === Field.NUMBER_12) return KIND_KEYS[raw] ? t(`vehicles:trip.kind.${KIND_KEYS[raw]}`) : raw;
    return isKm && /^\d+$/.test(raw) ? formatKmNumber(Number(raw)) : raw;
  };
  return t('vehicles:logbook.revision', {
    field: t(`vehicles:logbook.columns.${FIELD_KEYS[field] ?? 'startKm'}`),
    from: value(revision.oldValue),
    to: value(revision.newValue),
  });
}

const ISSUE_KEYS: Record<number, string> = {
  [Issue.NUMBER_1]: 'missingStartKm',
  [Issue.NUMBER_2]: 'missingEndKm',
  [Issue.NUMBER_3]: 'openTrip',
  [Issue.NUMBER_4]: 'noStart',
  [Issue.NUMBER_5]: 'endBelowStart',
  [Issue.NUMBER_6]: 'startBelowPrevious',
  [Issue.NUMBER_7]: 'edited',
  [Issue.NUMBER_8]: 'implausibleKm',
  [Issue.NUMBER_9]: 'notParked',
  [Issue.NUMBER_10]: 'outsideWorkingTime',
  [Issue.NUMBER_11]: 'missingLocation',
  [Issue.NUMBER_12]: 'missingPurpose',
  [Issue.NUMBER_13]: 'kmGap',
};

/**
 * Only for information, never an issue: changed by the administration, and a trip outside the
 * working time (FAFD-68) — the backend's LogbookIssues.NeedsAttention leaves both out as well.
 */
const INFO_ISSUES: number[] = [Issue.NUMBER_7, Issue.NUMBER_10];

/** Red: something does not add up. Yellow: only for information (edited, outside working time). */
export type IssueLevel = 'attention' | 'edited' | null;

export function issueLevel(entry: VehicleLogbookEntryDto): IssueLevel {
  const issues = entry.issues ?? [];
  if (issues.some((i) => !INFO_ISSUES.includes(i))) return 'attention';
  return issues.length > 0 ? 'edited' : null;
}

/** Readable texts of the entry's issues that need attention (the edit has its own history line). */
export function attentionTexts(entry: VehicleLogbookEntryDto, t: TFunction): string[] {
  return (entry.issues ?? [])
    .filter((i) => !INFO_ISSUES.includes(i))
    .map((i) => t(`vehicles:logbook.issues.${ISSUE_KEYS[i] ?? 'edited'}`));
}

/** Readable info notes — "Fahrt außerhalb der Arbeitszeit"; "Manuell geändert" has the history. */
export function infoTexts(entry: VehicleLogbookEntryDto, t: TFunction): string[] {
  return (entry.issues ?? [])
    .filter((i) => i === Issue.NUMBER_10)
    .map((i) => t(`vehicles:logbook.issues.${ISSUE_KEYS[i]}`));
}

/** "Anna Huber", or "Anna Huber → Ben Gruber" after a change of drivers. */
export function driversOf(entry: VehicleLogbookEntryDto): string {
  const start = entry.startedByName;
  const end = entry.endedByName;
  if (start && end && start !== end) return `${start} → ${end}`;
  return start ?? end ?? MISSING_NUMBER;
}
